import sumBy from 'lodash/sumBy'
import get from 'lodash/get'

import { capitalize } from '../string'
import numberConverter from '../tuition/numberForCurrency'

import getInsight from './getInsight'

const dataToWheel = (data = {}) => {
  // converts data from ML server response to NetChart format

  const links = []
  const nodes = []
  let id = 0

  // create the central node of the wheel
  nodes.push({
    id,
    is_education: 0,
    is_target: 0,
    style: {
      radius: 750,
    },
    type: 'central',
    name: 'Steppingblocks Career Wheel',
    loaded: true,
  })

  // create remaining nodes and connecting links
  data.forEach(v => {
    id += 1
    nodes.push({
      id,
      is_education: 0,
      is_target: 0,
      style: {
        radius: 250,
      },
      type: 'initial',
      name: '',
      loaded: true,
      skills: v.meta.skills ? v.meta.skills.slice(0, 5) : [],
    })
    links.push({
      id: `link${id}`,
      from: 0,
      to: id,
      type: 'edu_job',
    })

    get(v, 'meta.milestones', []).forEach(milestone => {
      const { company_or_university, is_target, level, major, milestone_title, salary, type } = milestone

      if ((milestone_title && milestone_title !== '') || (company_or_university && company_or_university !== '')) {
        id += 1
        let title = milestone_title ? capitalize(milestone_title) : ''
        if (type === 'education') {
          if (level && level !== '' && major && major !== '') title = `${capitalize(level)} of ${capitalize(major)}`
          else if ((!level || level === '') && major && major !== '') title = capitalize(major)
        }

        nodes.push({
          id,
          salary,
          degree_level: level,
          major,
          loaded: true,
          is_target,
          type,
          name: !title || title === 'Unspecified' ? `@${capitalize(company_or_university)}` : title,
          company_or_university: !title || title === 'Unspecified' || !company_or_university ? null : capitalize(company_or_university),
        })

        links.push({
          id: `link${id}`,
          from: id - 1,
          to: id,
          type: 'edu_job',
        })
      }
    })
  })

  return { links, nodes }
}

export const dataToGauge = (data = {}) => {
  const gaugeData = Object.keys(data).map(key => ({
    name: key,
    value: data[key],
  }))

  if (gaugeData.length === 2) gaugeData[1].value -= gaugeData[0].value

  return gaugeData
}

export const dataToGaugeFromZoom = (data = []) => {
  return [
    { value: get(data, 'subvalues[0].value', 0) },
    { value: sumBy(get(data, 'subvalues', []), 'value') - get(data, 'subvalues[0].value', 0) },
  ]
}

const getCommonMajorName = searchData => {
  const mostCommonMajor = get(searchData, 'mostCommonMajor')
  return mostCommonMajor ? mostCommonMajor.category : ''
}

export const dataToGaugePercentage = (data = []) =>
  data.length === 2 ? Math.round(Number((data[0].value * 100) / (data[0].value + data[1].value))) : 0

// const dataToWorkFieldDrillDown = (data = {}) => ({
//   subvalues: data.subvalues.map(subvalue => {
//     const newObj = subvalue
//     newObj.value = newObj.count
//     delete newObj.count
//     return newObj
//   }),
// })

const formatSalaryDistribution = (data = []) =>
  data && data.length
    ? data.map(d => {
        d.salary = `$${numberConverter(d.value)}`
        return d
      })
    : []

const getSalaryInsight = searchData => {
  const predictedSalary = get(searchData, 'predictedSalary', 0)

  return getInsight.salaryInsight(predictedSalary)
}

export const getEducationInsight = searchData => {
  const gaugeMajorPercentage = getGaugeMajorPercentage(searchData)
  const gaugeBachelorPercentage = getGaugeBachelorPercentage(searchData)
  const commonMajorName = getCommonMajorName(searchData)

  return getInsight.careerPage1(gaugeMajorPercentage, gaugeBachelorPercentage, commonMajorName)
}

const getGaugeMajorPercentage = searchData => {
  const gaugeMajorData = getGaugeMajorData(searchData)

  return gaugeMajorData ? dataToGaugePercentage(gaugeMajorData) : 0
}

const getGaugeBachelorPercentage = searchData => {
  const advancedDegreesData = get(searchData, 'advancedDegrees')

  return advancedDegreesData ? dataToGaugePercentage(advancedDegreesData) : 0
}

const getGaugeMajorData = searchData => {
  const mostCommonMajor = get(searchData, 'mostCommonMajor')
  return mostCommonMajor
    ? dataToGauge({
        a: mostCommonMajor[Object.keys(mostCommonMajor)[1]],
        b: mostCommonMajor[Object.keys(mostCommonMajor)[2]],
      })
    : []
}

export const getWheelData = careerPathData => {
  const wheelData = dataToWheel(careerPathData)

  return wheelData || {}
}

const getGaugeAdvancedData = searchData => {
  const advancedsearchData = get(searchData, 'advancedDegrees')
  const gaugeAdvancedData = advancedsearchData ? dataToGauge(advancedsearchData) : []

  return gaugeAdvancedData
}

const getSalaryDistribution = searchData => {
  const salaryDistributionData = get(searchData, 'salaryDistribution')
  const salaryDistribution = salaryDistributionData ? formatSalaryDistribution(salaryDistributionData) : []

  return salaryDistribution
}

export const transformCareersData = searchData => {
  const careerPathData = get(searchData, 'careerPathData', [])
  const gaugeBachelorPercentage = getGaugeBachelorPercentage(searchData)
  const gaugeMajorData = getGaugeMajorData(searchData)
  const wheelData = getWheelData(careerPathData)
  const gaugeAdvancedData = getGaugeAdvancedData(searchData)
  const salaryDistribution = getSalaryDistribution(searchData)
  const gaugeMajorPercentage = getGaugeMajorPercentage(searchData)
  const educationInsight = getEducationInsight(searchData)
  const salaryInsight = getSalaryInsight(searchData)

  return {
    gaugeMajorData,
    wheelData,
    gaugeAdvancedData,
    salaryDistribution,
    gaugeBachelorPercentage,
    gaugeMajorPercentage,
    educationInsight,
    salaryInsight,
  }
}



// WEBPACK FOOTER //
// ./src/utils/resumesearch/dataTransformation.js